/**
 * Icon Item List Component
 * Reusable list component for displaying items with icons in a grid layout
 * Used across Activities & Attractions, Amenities & Facilities, and Safety & Security sections
 */

import React from 'react';
import { spacing } from '@/design-system/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { IconWithLabel, IconWithLabelProps } from './IconWithLabel';

export interface IconItem {
  /** The label/text to display */
  label: string;
  /** Optional icon name/path from API */
  icon?: string | null;
  /** Optional ID for icon mapping (used for amenities) */
  id?: string;
  /** Unique key for React list rendering */
  key?: string;
}

export interface IconItemListProps {
  /** Array of items to display */
  items: IconItem[];
  /** Number of columns on mobile - defaults to 2 */
  mobileColumns?: number;
  /** Number of columns on desktop - defaults to 3 */
  desktopColumns?: number;
  /** Vertical gap between rows - defaults to spacing[4] (16px) */
  rowGap?: string;
  /** Horizontal gap between columns - defaults to spacing[6] (24px) */
  columnGap?: string;
  /** Icon size in pixels - defaults to 20 */
  iconSize?: number;
  /** Type of icon lookup - defaults to 'auto' */
  iconType?: 'activity' | 'amenity' | 'auto';
  /** Text color - defaults to primary */
  textColor?: string;
  /** Line height - defaults to normal */
  lineHeight?: 'normal' | 'relaxed';
  /** Custom color function for special cases */
  getCustomTextColor?: (label: string) => string | undefined;
  /** Custom color function for icon wrapper */
  getCustomIconColor?: (label: string) => string | undefined;
  /** Additional className for the grid container */
  className?: string;
}

/**
 * Icon Item List Component
 * Displays a grid of icon-with-label items with consistent spacing
 */
export const IconItemList: React.FC<IconItemListProps> = ({
  items,
  mobileColumns = 2,
  desktopColumns = 3,
  rowGap = spacing[4],
  columnGap = spacing[6],
  iconSize = 20,
  iconType = 'auto',
  textColor,
  lineHeight = 'normal',
  getCustomTextColor,
  getCustomIconColor,
  className,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? `repeat(${mobileColumns}, 1fr)`
          : `repeat(${desktopColumns}, 1fr)`,
        gap: `${rowGap} ${columnGap}`, // row-gap column-gap
      }}
    >
      {items.map((item, index) => {
        const iconProps: IconWithLabelProps = {
          label: item.label,
          icon: item.icon || null,
          id: item.id,
          iconSize,
          type: iconType,
          textColor,
          lineHeight,
          getCustomTextColor,
          getCustomIconColor,
        };

        return (
          <IconWithLabel
            key={item.key || item.id || `${item.label}-${index}`}
            {...iconProps}
          />
        );
      })}
    </div>
  );
};
